import { APIFieldType, type ArdoqId } from '@ardoq/api-types';
import type { PermissionContext } from '@ardoq/access-control';
import type { FormattingAndLabelRules } from '@ardoq/data-model';
import type { LoadedGraphWithViewpointMode } from '@ardoq/graph';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import { fieldUtils } from '@ardoq/scope-data';
import {
  toPolarPlot,
  type PolarDatum,
  type PolarViewSettings,
  type RadialPlot,
} from '@ardoq/xyz-charts';
import {
  fieldsInScope,
  numericFieldsInScope,
  readFieldValueFromScopeData,
} from 'streams/fields/numericFieldsInScope';
import { toComponentLabels } from 'tabview/chartShared/componentLabels';
import { toFieldLabel, toFieldOptions } from 'tabview/chartShared/fieldLabel';
import type {
  ChartEmptyReason,
  FieldOption,
  NumericField,
} from 'tabview/chartShared/types';

const SERIES_FIELD_TYPES = [
  APIFieldType.LIST,
  APIFieldType.SELECT_MULTIPLE_LIST,
  APIFieldType.CHECKBOX,
  APIFieldType.TEXT,
];

export type PolarSelection = Pick<
  PolarViewSettings,
  | 'selectedFieldNameAngle'
  | 'selectedFieldNameRadius'
  | 'selectedFieldNameSeries'
>;

export type PolarSourceDataArgs = {
  viewSettings: PolarSelection;
  loadedGraph: LoadedGraphWithViewpointMode;
  permissionContext: PermissionContext;
  locale: Locale;
  i18nLocale: I18nLocale;
  formattingAndLabelRules: FormattingAndLabelRules;
};

export type PolarSourceData = {
  viewInstanceId: string;
  plot: RadialPlot;
  points: { componentId: ArdoqId }[];
  excludedCount: number;
  numericFields: NumericField[];
  seriesFields: FieldOption[];
  angleTitle: string;
  radiusTitle: string;
  isViewEmpty: boolean;
  emptyReason: ChartEmptyReason;
  locale: Locale;
  i18nLocale: I18nLocale;
};

export type SeriesLabels = {
  allComponents: string;
  noValue: string;
  yes: string;
  no: string;
};

export type PolarComponent = {
  id: ArdoqId;
  label: string;
  angleValue: number | null;
  radiusValue: number | null;
  seriesValue: unknown;
};

export type SeriesIdentity = { key: string; label: string };

/**
 * The series a component falls into, by its value on the series field. Keys are namespaced by kind,
 * so a value that reads like a sentinel ("No value") never joins the sentinel's series, and a
 * multi-select value keys the same whatever order its options were picked in.
 */
export const toSeriesIdentity = (
  value: unknown,
  labels: SeriesLabels
): SeriesIdentity => {
  if (typeof value === 'boolean') {
    return { key: `checkbox:${value}`, label: value ? labels.yes : labels.no };
  }
  if (Array.isArray(value) && value.length > 0) {
    const options = value.map(String).toSorted();
    return {
      key: `list:${JSON.stringify(options)}`,
      label: options.join(', '),
    };
  }
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    Array.isArray(value)
  ) {
    return { key: 'none', label: labels.noValue };
  }
  return { key: `value:${String(value)}`, label: String(value) };
};

type PolarPlotInput = {
  data: PolarDatum[];
  excludedCount: number;
};

const toDatum = (
  { id, label, angleValue, radiusValue, seriesValue }: PolarComponent,
  hasSeriesField: boolean,
  labels: SeriesLabels
): PolarDatum[] => {
  if (angleValue === null || radiusValue === null) return [];
  const series = hasSeriesField
    ? toSeriesIdentity(seriesValue, labels)
    : { key: 'all', label: labels.allComponents };
  return [
    {
      key: id,
      label,
      seriesKey: series.key,
      seriesLabel: series.label,
      angleValue,
      radiusValue,
    },
  ];
};

export const toPolarPlotInput = (
  components: PolarComponent[],
  hasSeriesField: boolean,
  labels: SeriesLabels
): PolarPlotInput => {
  const data = components.flatMap(component =>
    toDatum(component, hasSeriesField, labels)
  );
  return { data, excludedCount: components.length - data.length };
};

const toSeriesLabels = (i18nLocale: I18nLocale): SeriesLabels => ({
  allComponents: t(i18nLocale, 'views:polar.series.all', 'All components'),
  noValue: t(i18nLocale, 'views:polar.series.noValue', 'No value'),
  yes: t(i18nLocale, 'views:polar.series.yes', 'Yes'),
  no: t(i18nLocale, 'views:polar.series.no', 'No'),
});

const isOffered = (
  fields: { name: string }[],
  fieldName: string | null
): fieldName is string =>
  fieldName !== null && fields.some(({ name }) => name === fieldName);

export const buildPolarSourceData = (
  viewInstanceId: string,
  {
    viewSettings: {
      selectedFieldNameAngle,
      selectedFieldNameRadius,
      selectedFieldNameSeries,
    },
    loadedGraph,
    permissionContext,
    locale,
    i18nLocale,
    formattingAndLabelRules,
  }: PolarSourceDataArgs
): PolarSourceData => {
  const { scopeData, scopeComponentIds } = loadedGraph;
  const numericFields = toFieldOptions(
    numericFieldsInScope(scopeData, permissionContext)
  );
  const seriesFields = toFieldOptions(
    fieldsInScope(scopeData, permissionContext, SERIES_FIELD_TYPES)
  );
  const angleTitle = toFieldLabel(numericFields, selectedFieldNameAngle);
  const radiusTitle = toFieldLabel(numericFields, selectedFieldNameRadius);
  const base = {
    viewInstanceId,
    numericFields,
    seriesFields,
    angleTitle,
    radiusTitle,
    locale,
    i18nLocale,
  };
  const empty = (emptyReason: ChartEmptyReason): PolarSourceData => ({
    ...base,
    plot: toPolarPlot({ data: [], angleTitle, radialTitle: radiusTitle }),
    points: [],
    excludedCount: 0,
    isViewEmpty: true,
    emptyReason,
  });

  if (scopeComponentIds.length === 0) return empty('no components');
  if (numericFields.length === 0) return empty('no numeric fields');
  if (
    !isOffered(numericFields, selectedFieldNameAngle) ||
    !isOffered(numericFields, selectedFieldNameRadius)
  ) {
    return empty('no fields selected');
  }

  const hasSeriesField = isOffered(seriesFields, selectedFieldNameSeries);
  const labels = toComponentLabels({
    enhancedScopeData: scopeData,
    componentIds: scopeComponentIds,
    formattingAndLabelRules,
    i18nLocale,
  });
  const readNumber = readFieldValueFromScopeData(scopeData);
  const components = scopeComponentIds.map(id => ({
    id,
    label: labels.get(id) ?? '',
    angleValue: readNumber(id, selectedFieldNameAngle),
    radiusValue: readNumber(id, selectedFieldNameRadius),
    seriesValue: hasSeriesField
      ? fieldUtils.getFieldValueForComponentOrReference(
          scopeData,
          id,
          selectedFieldNameSeries
        )
      : null,
  }));

  const { data, excludedCount } = toPolarPlotInput(
    components,
    hasSeriesField,
    toSeriesLabels(i18nLocale)
  );
  const emptyReason = data.length === 0 ? 'too few positions' : null;

  return {
    ...base,
    plot: toPolarPlot({ data, angleTitle, radialTitle: radiusTitle }),
    points: data.map(({ key }) => ({ componentId: key })),
    excludedCount,
    isViewEmpty: emptyReason !== null,
    emptyReason,
  };
};
